import { isAudit, isEngagement } from "PFApp/activities/helpers";
import { DocumentButtons } from "PFApp/components/document_buttons";
import { TemplateKey } from "PFApp/constants/templates";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { Button } from "PFComponents/button";
import ButtonGroup from "PFComponents/button_group/button_group";
import { Inline } from "PFComponents/containers";
import { DropdownButton } from "PFComponents/dropdown_button/dropdown_button";
import { Notification, NotificationCategoryType } from "PFCore/types";
import { DocFormat } from "PFTypes/doc_format";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import css from "./notification_buttons.module.scss";
import { checkNotificationHasQuickLinks, getOpenActivityLabelKey } from "./notification_buttons.utils";

type NotificationButtonsProps = {
  notification: Notification;
  primaryActionRef?: RefObject<HTMLButtonElement | null>;
  onButtonClick?: () => void;
};

export const NotificationButtons = ({
  notification,
  primaryActionRef,
  onButtonClick
}: NotificationButtonsProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "notifications" });
  const navigate = useNavigate();
  const { isEnabled: isBookingModuleEnabled, isPermittedToAccess } = useBookingModulePermissions();
  const { categoryType, urlAddress, activityId, metadata } = notification;

  const showDownloadButton = categoryType === NotificationCategoryType.FileGenerated && urlAddress;
  const showQuickLinks = checkNotificationHasQuickLinks(categoryType);

  if (!showDownloadButton && !showQuickLinks) {
    return null;
  }

  const templateKey = (metadata?.templateKey || "") as TemplateKey;
  const isAuditNotification = isAudit(templateKey);
  const isEngagementNotification = isEngagement(templateKey);

  const openActivityLabelKey = getOpenActivityLabelKey(isEngagementNotification, isAuditNotification);

  const quickLinkOptions = [
    {
      id: "open-role-engagement",
      label: t(openActivityLabelKey),
      onClick: () => navigate(`/activities/${activityId}`)
    },
    ...(isBookingModuleEnabled && isPermittedToAccess
      ? [
          {
            id: "open-booking-module",
            label: t("openBookingModule"),
            onClick: () => navigate(`/booking?activityId=${activityId}`)
          }
        ]
      : [])
  ].map(({ id, label, onClick }) => ({ id, item: onClick, displayElement: label }));

  return (
    <Inline justifyContent="end">
      {showDownloadButton && (
        <DocumentButtons
          generateDocument={() => Promise.resolve({ fileUrl: urlAddress })}
          buttonComponent={({ onClick }) => (
            <Button
              ref={primaryActionRef}
              text={t("download")}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onClick(DocFormat.Csv);
                onButtonClick?.();
              }}
              small
            />
          )}
          bypassPermissionCheck
        />
      )}
      {showQuickLinks && (
        <ButtonGroup compact autoWidth>
          <Button
            kind="primary"
            text={t("openHistory")}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/activities/${activityId}/history`);
              onButtonClick?.();
            }}
            small
          />
          <DropdownButton
            small
            buttonClassName={css.dropdownButton}
            buttonKind="primary"
            icon="chevron-down"
            aria-label={t("moreActions")}
            options={quickLinkOptions}
            handleClick={(e) => e.stopPropagation()}
            handleChange={(item) => {
              item?.();
              onButtonClick?.();
            }}
          />
        </ButtonGroup>
      )}
    </Inline>
  );
};
